import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { copyToClipboard } from '../../lib/clipboard';
import { CalendarDays, X, Printer, Loader2, CheckCircle2, Plus, RefreshCw, ShieldCheck, Banknote, Copy, Check, Star, MessageSquareQuote, MapPin, CreditCard } from 'lucide-react';
import { Link } from 'react-router-dom';
import PrintSignatureBlock from '../../components/PrintSignatureBlock';
import { CurrencyPreview } from '../../components/CurrencyPreview';
import { wallDateTime } from '../../lib/time';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const METHOD_LABEL = { blik: 'BLIK', cash: 'Cash', card: 'Card' };

export default function PatientAppointments() {
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState({ prepay_enabled: false, prepay_percent: 50, payment_methods: {} });
  const [pollMsg, setPollMsg] = useState('');
  const [followUpDismissed, setFollowUpDismissed] = useState(0);
  const [activeModal, setActiveModal] = useState(null);      // appointment_id being paid
  const [intent, setIntent] = useState(null);                 // response from /prepay
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [myReviews, setMyReviews] = useState({});  // { appointment_id: review }

  const load = () => api.get('/appointments').then(r => setItems(r.data));
  useEffect(() => {
    load();
    api.get('/clinic/settings').then(r => setSettings(r.data));
    api.get('/reviews/mine').then(r => {
      const map = {};
      (r.data || []).forEach(rv => { map[rv.appointment_id] = rv; });
      setMyReviews(map);
    }).catch(() => {});
    // Returning from Stripe Checkout — reconcile the card payment.
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    const apptId = params.get('appt');
    const sessionId = params.get('session_id');
    if (payment === 'success' && apptId && sessionId) {
      api.get(`/appointments/${apptId}/prepay/card-status`, { params: { session_id: sessionId } })
        .then(r => {
          setPollMsg(r.data.prepay_status === 'paid'
            ? 'Card payment received — your slot is confirmed.'
            : 'Payment is processing — your slot will be confirmed shortly.');
          load();
        })
        .catch(() => setPollMsg('We could not verify the payment yet — it will update automatically.'));
      window.history.replaceState({}, '', window.location.pathname);
    } else if (payment === 'cancelled') {
      setPollMsg('Card payment was cancelled — you can try again any time.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const refreshReviews = () => api.get('/reviews/mine').then(r => {
    const map = {};
    (r.data || []).forEach(rv => { map[rv.appointment_id] = rv; });
    setMyReviews(map);
  });

  const cancel = async (a) => {
    try {
      const { data } = await api.post(`/appointments/${a.appointment_id}/cancel`);
      const msg = data.auto_refunded
        ? `Cancelled. Your card prepayment was refunded automatically — it should appear on your statement within 5–10 business days.`
        : data.refund_eligible
        ? `Cancelled. The clinic will refund your prepayment (cancelled ${data.hours_until.toFixed(1)}h before ≥ ${data.policy_hours}h policy).`
        : `Cancelled. This is inside the ${data.policy_hours}h no-refund window; prepayments are kept.`;
      setPollMsg(msg);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  const reschedule = async (a) => {
    const next = window.prompt('New date & time (YYYY-MM-DDTHH:MM):', a.starts_at?.slice(0,16));
    if (!next) return;
    try {
      await api.put(`/appointments/${a.appointment_id}/reschedule`, { starts_at: next });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  const pickMethod = async (a, method) => {
    setBusy(true);
    setReference('');
    try {
      const { data } = await api.post(`/appointments/${a.appointment_id}/prepay`, { method });
      if (method === 'cash') {
        setPollMsg(data.instructions
          ? `Cash on arrival — ${data.instructions}`
          : 'Cash on arrival recorded. Just bring payment to your session.');
        await load();
      } else {
        setIntent({ ...data, appointment_id: a.appointment_id });
        setActiveModal(a.appointment_id);
        await load();
      }
    } catch (e) {
      alert('Could not start: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const payCard = async (a) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/appointments/${a.appointment_id}/prepay/card-checkout`);
      window.location.href = data.checkout_url;
    } catch (e) {
      alert('Could not start card payment: ' + (e.response?.data?.detail || e.message));
      setBusy(false);
    }
  };

  const confirmPaid = async () => {
    if (!intent) return;
    setBusy(true);
    try {
      const fd = new FormData();
      if (reference) fd.append('reference', reference);
      await api.post(`/appointments/${intent.appointment_id}/confirm-payment`, fd);
      setPollMsg(`Thanks! We'll verify your ${METHOD_LABEL[intent.method]} transfer shortly and confirm your slot.`);
      setActiveModal(null); setIntent(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const methods = Object.entries(settings.payment_methods || {}).filter(([_, v]) => v).map(([k]) => k);

  // Iter 139 — follow-up rebooking nudge: latest appointment completed in
  // the last 14 days, no upcoming booking, not dismissed yet.
  const followUp = React.useMemo(() => {
    if (items.some(a => a.status === 'scheduled' || a.status === 'pending_approval')) return null;
    const last = items
      .filter(a => a.status === 'completed' && a.starts_at)
      .sort((a, b) => (b.starts_at || '').localeCompare(a.starts_at || ''))[0];
    if (!last) return null;
    const days = (Date.now() - new Date(last.starts_at.slice(0, 10)).getTime()) / 86400000;
    if (days > 14) return null;
    try { if (localStorage.getItem(`followup-dismissed-${last.appointment_id}`)) return null; } catch (e) {}
    return last;
  }, [items, followUpDismissed]);

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">YOUR CALENDAR</div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Appointments</h1>
        </div>
        <div className="flex gap-2">
          <Link to="/patient/book" className="btn-primary inline-flex items-center gap-2 print:hidden" data-testid="book-appt-link">
            <Plus className="w-4 h-4"/> Book appointment
          </Link>
          <button onClick={()=>window.print()} className="btn-secondary inline-flex items-center gap-2 print:hidden" data-testid="print-appointments">
            <Printer className="w-4 h-4"/> Print
          </button>
        </div>
      </div>

      {pollMsg && (
        <div className="bento-card mb-4 flex items-center gap-3" data-testid="prepay-status">
          <CheckCircle2 className="w-5 h-5 text-[#3A664D]"/>
          <span className="text-sm">{pollMsg}</span>
        </div>
      )}

      {followUp && (
        <div className="bento-card mb-4 border-l-4 border-l-brand flex flex-wrap items-center gap-3" data-testid="followup-banner">
          <RefreshCw className="w-5 h-5 text-brand shrink-0"/>
          <div className="flex-1 min-w-[220px]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-brand font-medium">HOW DID IT GO?</div>
            <div className="text-sm mt-0.5">
              Your last visit{followUp.therapist_name ? <> with <strong>{followUp.therapist_name}</strong></> : null} is complete —
              keep the momentum going and book your follow-up.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to={`/patient/book${followUp.clinician_id ? `?clinician=${followUp.clinician_id}` : ''}`}
                  className="btn-primary text-sm inline-flex items-center gap-1.5" data-testid="followup-book-btn">
              <CalendarDays className="w-4 h-4"/> Book follow-up
            </Link>
            <button type="button" className="text-fg-muted hover:text-fg" aria-label="Dismiss"
                    onClick={() => {
                      try { localStorage.setItem(`followup-dismissed-${followUp.appointment_id}`, '1'); } catch (e) {}
                      setFollowUpDismissed(x => x + 1);
                    }}
                    data-testid="followup-dismiss">
              <X className="w-4 h-4"/>
            </button>
          </div>
        </div>
      )}

      {items.some(a => a.status === 'pending_approval') && (
        <div className="bento-card mb-4 border-l-4 border-l-[#F2B33D] bg-[#FFF8E6]" data-testid="pending-banner">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A4A00] font-medium">AWAITING CONFIRMATION</div>
          <div className="text-sm text-[#5C5040] mt-1">
            One or more of your bookings is being reviewed by the clinic. You'll receive an email as soon as it's confirmed (usually within an hour during business hours).
          </div>
        </div>
      )}

      <div className="bento-card">
        {items.map(a => {
          const needsPrepay = settings.prepay_enabled && a.status === 'scheduled' && a.price > 0
                              && !['paid', 'pay_on_arrival'].includes(a.prepay_status);
          const percent = a.prepay_percent || settings.prepay_percent || 50;
          const amount = (a.price * percent / 100).toFixed(2);
          return (
            <div key={a.appointment_id} className="py-4 border-b border-soft last:border-0" data-testid={`patient-appt-${a.appointment_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-md bg-[#F2EBDB] flex items-center justify-center"><CalendarDays className="w-5 h-5 text-brand" /></div>
                  <div>
                    <div className="font-medium">{a.service_name || 'Session'} · {a.therapist_name}</div>
                    <div className="text-xs text-fg-muted">{wallDateTime(a.starts_at, settings?.clock_format || '24h')} · {a.duration_min} min · {a.price} {a.currency}</div>
                    {a.location_name && (
                      <div className="text-[11px] text-fg-muted mt-0.5 inline-flex items-center gap-1" data-testid={`appt-location-${a.appointment_id}`}>
                        <MapPin className="w-3 h-3 text-brand"/>
                        <strong>{a.location_name}</strong>
                        {a.location_address && <span className="opacity-80">· {a.location_address}</span>}
                      </div>
                    )}
                    {a.reason_for_visit && (
                      <div className={`text-xs italic mt-1.5 px-2 py-1 rounded inline-block ${a.new_condition ? 'bg-[#FFF4D6] text-[#5A3A00] border border-[#FFB000]/40' : 'bg-[var(--bg-tertiary)] text-fg-secondary'}`}
                           data-testid={`appt-reason-${a.appointment_id}`}>
                        {a.new_condition && <span className="font-bold not-italic mr-1">NEW CONDITION:</span>}
                        "{a.reason_for_visit}"
                      </div>
                    )}
                    {a.notes && <div className="text-xs text-fg-secondary mt-1">{a.notes}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap justify-end">
                  <Status s={a.status} />
                  <PrepayBadge a={a} />
                  {a.status === 'scheduled' && (
                    <>
                      <button onClick={()=>reschedule(a)} className="text-brand inline-flex items-center gap-1 text-sm print:hidden" data-testid={`reschedule-${a.appointment_id}`}>
                        <RefreshCw className="w-4 h-4"/> Reschedule
                      </button>
                      <button onClick={()=>cancel(a)} className="text-[#B83A3A] inline-flex items-center gap-1 text-sm print:hidden" data-testid={`cancel-${a.appointment_id}`}>
                        <X className="w-4 h-4"/> Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>

              {needsPrepay && (
                <div className="mt-3 surface border border-soft rounded-lg p-3 print:hidden" data-testid={`prepay-row-${a.appointment_id}`}>
                  <UnpaidReleaseWarning a={a} minutes={settings.unpaid_release_minutes ?? 30}/>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="text-xs">
                      <div className="text-fg-muted">Prepay {percent}% to confirm</div>
                      <div className="font-heading font-bold text-lg">
                        {amount} <span className="text-xs font-normal text-fg-muted">{a.currency}</span>
                      </div>
                    </div>
                    <div className="h-8 w-px bg-soft hidden md:block"/>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-fg-muted mr-1">Pay with</span>
                      {methods.includes('card') && (
                        <button onClick={()=>payCard(a)} disabled={busy}
                                className="btn-secondary text-xs inline-flex items-center gap-1" data-testid={`pay-card-${a.appointment_id}`}>
                          <CreditCard className="w-3.5 h-3.5 text-[#003D80]"/> Card (Visa/Mastercard)
                        </button>
                      )}
                      {methods.includes('blik') && (
                        <button onClick={()=>pickMethod(a, 'blik')} disabled={busy}
                                className="btn-secondary text-xs inline-flex items-center gap-1" data-testid={`pay-blik-${a.appointment_id}`}>
                          <ShieldCheck className="w-3.5 h-3.5 text-[#E4007F]"/> BLIK
                        </button>
                      )}
                      {methods.includes('cash') && !settings.prepay_electronic_only && (
                        <button onClick={()=>pickMethod(a, 'cash')} disabled={busy}
                                className="btn-secondary text-xs inline-flex items-center gap-1" data-testid={`pay-cash-${a.appointment_id}`}>
                          <Banknote className="w-3.5 h-3.5 text-[#3A664D]"/> Cash on arrival
                        </button>
                      )}
                      {settings.prepay_electronic_only && (
                        <span className="text-[10px] text-fg-muted" data-testid={`electronic-only-note-${a.appointment_id}`}>
                          Electronic prepayment required (BLIK or card)
                        </span>
                      )}
                    </div>
                  </div>
                  <CurrencyPreview amount={parseFloat(amount)} currency={a.currency} apptId={a.appointment_id}/>
                  {a.prepay_status === 'awaiting_payment' && a.prepay_method === 'card' && (
                    <div className="mt-2 text-xs text-fg-muted">
                      <span className="font-medium">Card payment started.</span> Finish it in the Stripe page, or
                      <button onClick={()=>payCard(a)} disabled={busy}
                              className="ml-1 text-brand underline" data-testid={`retry-card-${a.appointment_id}`}>
                        try again →
                      </button>
                    </div>
                  )}
                  {a.prepay_status === 'awaiting_payment' && a.prepay_method !== 'card' && (
                    <div className="mt-2 text-xs text-fg-muted">
                      <span className="font-medium">Awaiting your {METHOD_LABEL[a.prepay_method] || 'payment'}.</span> Send the transfer, then click "I've paid".
                      <button onClick={()=>{ setIntent({ method: a.prepay_method, amount: a.prepay_amount, currency: a.prepay_currency, percent: a.prepay_percent, instructions: null, appointment_id: a.appointment_id }); setActiveModal(a.appointment_id); }}
                              className="ml-2 text-brand underline" data-testid={`open-ive-paid-${a.appointment_id}`}>
                        I've paid →
                      </button>
                    </div>
                  )}
                  {a.prepay_status === 'pending_verification' && (
                    <div className="mt-2 text-xs text-[#FFB000]">
                      Waiting for the clinic to verify your {METHOD_LABEL[a.prepay_method] || 'transfer'}. This usually takes under an hour during business hours.
                    </div>
                  )}
                </div>
              )}
              {a.status === 'completed' && (
                <ReviewSection
                  appointment={a}
                  existing={myReviews[a.appointment_id]}
                  onSubmitted={refreshReviews}
                />
              )}
            </div>
          );
        })}
        {!items.length && <div className="text-sm text-fg-muted py-6 text-center">No appointments yet.</div>}
      </div>

      <PrintSignatureBlock leftLabel="Clinic representative" rightLabel="Patient"/>

      {activeModal && intent && (
        <PaymentModal
          intent={intent}
          settings={settings}
          reference={reference}
          setReference={setReference}
          busy={busy}
          onClose={() => { setActiveModal(null); setIntent(null); }}
          onConfirm={confirmPaid}
        />
      )}
    </div>
  );
}

// Popular ECB-covered currencies moved to components/CurrencyPreview.jsx.

function UnpaidReleaseWarning({ a, minutes }) {
  // Only self-booked slots are auto-released; staff bookings keep their hold.
  if (a.booked_by !== 'patient' || !minutes) return null;
  if (!['pending', 'awaiting_payment'].includes(a.prepay_status)) return null;
  const created = a.created_at ? new Date(a.created_at) : null;
  const left = created ? Math.round(minutes - (Date.now() - created.getTime()) / 60000) : null;
  return (
    <div className="mb-2 flex items-start gap-1.5 text-xs text-[#A86F31] bg-[#FFB000]/10 border border-[#FFB000]/30 rounded-md px-2.5 py-1.5"
         data-testid={`unpaid-release-warning-${a.appointment_id}`}>
      <span className="font-bold">⏱</span>
      <span>
        <strong>Pay within {minutes} minutes of booking</strong> or this slot is released automatically and
        offered to the waitlist.
        {left !== null && left > 0 && <> Around <strong>{left} min</strong> remaining.</>}
        {left !== null && left <= 0 && <> This slot may be released at any moment — pay now to keep it.</>}
      </span>
    </div>
  );
}

function PrepayBadge({ a }) {
  const s = a.prepay_status;
  if (s === 'paid') return <span className="badge-success" data-testid="prepay-badge-paid">Prepay paid</span>;
  if (s === 'pending_verification') return <span className="badge-warn" data-testid="prepay-badge-pending">Awaiting verify</span>;
  if (s === 'awaiting_payment') return <span className="badge-warn" data-testid="prepay-badge-awaiting">Awaiting transfer</span>;
  if (s === 'pay_on_arrival') return <span className="badge-neutral" data-testid="prepay-badge-cash">Cash on arrival</span>;
  if (s === 'refunded') return <span className="badge-neutral" data-testid="prepay-badge-refunded">Refunded</span>;
  return null;
}

function Status({ s }) {
  if (s === 'pending_approval') {
    return <span className="badge-warn" data-testid="status-pending-approval" title="The clinic will review your request and confirm shortly.">Awaiting approval</span>;
  }
  const cls = s === 'completed' ? 'badge-success' : s === 'scheduled' ? 'badge-neutral'
              : s === 'cancelled' ? 'badge-error' : 'badge-warn';
  return <span className={cls}>{s}</span>;
}

function ReviewSection({ appointment, existing, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  if (existing) {
    const statusCopy = {
      pending: { label: 'Submitted · awaiting moderation', cls: 'text-[#8A4A00] bg-[#FFF4E0]' },
      approved: { label: 'Published — thank you!', cls: 'text-[#3A664D] bg-[#E8F5EC]' },
      rejected: { label: "Couldn't be published", cls: 'text-[#B83A3A] bg-[#FBE9E5]' },
    }[existing.status] || { label: existing.status, cls: 'bg-surface-2 text-fg-muted' };
    return (
      <div className={`mt-3 rounded-lg p-3 text-xs ${statusCopy.cls}`} data-testid={`review-status-${appointment.appointment_id}`}>
        <div className="flex items-center gap-2 font-medium">
          <MessageSquareQuote className="w-3.5 h-3.5"/> Your review · {statusCopy.label}
        </div>
        <div className="mt-1.5 flex items-center gap-2">
          {[1,2,3,4,5].map(n => (
            <Star key={n} className={`w-3.5 h-3.5 ${n <= existing.rating ? 'fill-[#FFB000] text-[#FFB000]' : 'text-current opacity-30'}`}/>
          ))}
        </div>
        <p className="mt-1.5 italic opacity-90">"{existing.text}"</p>
      </div>
    );
  }

  if (!open) {
    return (
      <button onClick={()=>setOpen(true)}
              className="mt-3 text-xs text-brand inline-flex items-center gap-1.5 underline print:hidden"
              data-testid={`leave-review-${appointment.appointment_id}`}>
        <MessageSquareQuote className="w-3.5 h-3.5"/> Leave a review
      </button>
    );
  }

  const submit = async () => {
    if (text.trim().length < 4) { setErr('Please write a few words about your visit.'); return; }
    setBusy(true); setErr('');
    try {
      await api.post('/reviews', {
        appointment_id: appointment.appointment_id,
        rating, text: text.trim(),
      });
      setOpen(false); setText(''); setRating(5);
      onSubmitted?.();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 surface border border-soft rounded-lg p-3 print:hidden" data-testid={`review-form-${appointment.appointment_id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-medium inline-flex items-center gap-1.5">
          <MessageSquareQuote className="w-3.5 h-3.5 text-brand"/> Share your experience
        </div>
        <button onClick={()=>setOpen(false)} className="text-fg-muted text-xs">Cancel</button>
      </div>
      <div className="flex items-center gap-1 mb-2" onMouseLeave={()=>setHover(0)}>
        {[1,2,3,4,5].map(n => (
          <button key={n} type="button" onClick={()=>setRating(n)} onMouseEnter={()=>setHover(n)}
                  data-testid={`star-${appointment.appointment_id}-${n}`}>
            <Star className={`w-6 h-6 ${n <= (hover || rating) ? 'fill-[#FFB000] text-[#FFB000]' : 'text-[#E8DFC8]'}`}/>
          </button>
        ))}
        <span className="text-xs text-fg-muted ml-2">{rating}/5</span>
      </div>
      <textarea value={text} onChange={e=>setText(e.target.value)}
                placeholder="What worked well? Anything we could improve?"
                className="input text-xs min-h-[80px]" maxLength={2000}
                data-testid={`review-text-${appointment.appointment_id}`}/>
      <div className="text-[10px] text-fg-muted mt-1">Your review will be moderated by the clinic before it appears publicly. Your name will show as first name + last initial only.</div>
      {err && <div className="mt-1 text-xs text-[#B83A3A]" data-testid={`review-err-${appointment.appointment_id}`}>{err}</div>}
      <div className="mt-2 flex justify-end gap-2">
        <button onClick={submit} disabled={busy}
                className="btn-primary text-xs inline-flex items-center gap-1.5"
                data-testid={`review-submit-${appointment.appointment_id}`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Star className="w-3.5 h-3.5"/>}
          Submit review
        </button>
      </div>
    </div>
  );
}

function PaymentModal({ intent, settings, reference, setReference, busy, onClose, onConfirm }) {
  const isCash = intent.method === 'cash';
  const label = METHOD_LABEL[intent.method];
  const detail = intent.method === 'blik'
    ? `BLIK phone: ${settings.blik_phone || '(ask the clinic)'}`
    : 'Cash on arrival';
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center p-4" data-testid="pay-modal">
      <div className="surface border border-soft rounded-2xl shadow-xl w-full max-w-lg p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-accent font-semibold">Prepayment · {label}</div>
            <div className="font-heading font-bold text-2xl mt-1">
              {intent.amount?.toFixed(2)} <span className="text-sm text-fg-muted">{intent.currency}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-fg-muted hover:text-[#B83A3A]" data-testid="pay-modal-close">
            <X className="w-5 h-5"/>
          </button>
        </div>

        {isCash ? (
          <>
            <div className="surface border border-soft rounded-lg p-4 text-sm">
              <Banknote className="w-5 h-5 text-[#3A664D] mb-2"/>
              <div className="font-medium mb-1">Cash on arrival</div>
              <p className="text-fg-muted">
                {settings.payment_notes_cash || 'Just bring the payment to your session. If possible, have exact change.'}
              </p>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={onClose} className="btn-secondary" data-testid="pay-cash-close">Got it</button>
            </div>
          </>
        ) : (
          <>
            <div className="surface border border-soft rounded-lg p-4 text-sm mb-3" data-testid="pay-instructions">
              <div className="flex gap-4 items-start">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-fg-muted mb-1">Send to</div>
                  <div className="font-mono font-semibold text-base flex items-center gap-2">
                    <span>{detail}</span>
                    <CopyButton value={(settings.blik_phone || '').trim()} label="Copy phone" testid="copy-blik-phone" />
                  </div>
                  {intent.instructions && (
                    <p className="text-fg-muted mt-2">{intent.instructions}</p>
                  )}
                  <div className="text-[11px] text-fg-muted mt-3 leading-relaxed">
                    Scan the QR with your banking app to pre-fill the BLIK transfer — then enter the
                    <span className="font-medium text-fg"> {intent.amount?.toFixed(2)} {intent.currency}</span>.
                  </div>
                </div>
                <div className="shrink-0">
                  <div className="rounded-lg border border-soft bg-white p-2" title="BLIK recipient QR">
                    <img
                      src={`${BACKEND}/api/payments/blik-qr?phone=${encodeURIComponent(settings.blik_phone || '')}&amount=${intent.amount || ''}&ref=${encodeURIComponent(intent.appointment_id || '')}`}
                      alt="BLIK QR"
                      className="w-32 h-32 block"
                      data-testid="blik-qr-img"
                    />
                  </div>
                </div>
              </div>
            </div>
            <label className="block text-xs text-fg-muted mb-1">Transfer reference / BLIK code (optional)</label>
            <input
              value={reference}
              onChange={e => setReference(e.target.value)}
              placeholder="e.g. 123456 · Appointment 15 Mar 10:00"
              className="input w-full text-sm"
              data-testid="pay-reference"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={onClose} className="btn-secondary text-sm" data-testid="pay-modal-cancel">Cancel</button>
              <button onClick={onConfirm} disabled={busy}
                      className="btn-primary text-sm inline-flex items-center gap-2" data-testid="pay-modal-confirm">
                {busy && <Loader2 className="w-4 h-4 animate-spin"/>}
                I've paid
              </button>
            </div>
            <p className="text-[11px] text-fg-muted mt-3">
              The clinic will verify your transfer and confirm your slot — usually within the hour during business hours.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function CopyButton({ value, label = 'Copy', testid }) {
  const [done, setDone] = useState(false);
  if (!value) return null;
  const copy = async () => {
    const ok = await copyToClipboard(value, { silent: true });
    if (ok) { setDone(true); setTimeout(() => setDone(false), 1500); }
  };
  return (
    <button type="button" onClick={copy} aria-label={label} title={label}
            className="text-fg-muted hover:text-brand inline-flex items-center gap-1 text-xs"
            data-testid={testid}>
      {done ? <Check className="w-3.5 h-3.5 text-[#3A664D]"/> : <Copy className="w-3.5 h-3.5"/>}
    </button>
  );
}
